import type { LearningsPageResponse } from '@@/api/learnings/page/page-types';
import type { LearningsAskManager } from '@/state/learnings/useLearningsAsk';
import type { LearningsScreenManager } from '@/state/learnings/useLearningsScreen';
import { useLearningsAsk } from '@/state/learnings/useLearningsAsk';
import { useLearningsPage } from '@/state/learnings/useLearningsPage';
import { useLearningsScreen } from '@/state/learnings/useLearningsScreen';
import { useLearningsSetup } from '@/state/learnings/useLearningsSetup';
import { useTestDebrief } from '@/state/learnings/useTestDebrief';
import { useTourScreen } from '@/tours';
import { EmptyState } from '../../ConsolePanel';
import { AskField } from './AskField';
import { DebriefView } from './DebriefView';
import { EvidenceView } from './EvidenceView';
import { FeedView } from './FeedView';
import { LearningsAskPanel } from './LearningsAskPanel';
import { OpportunitiesView } from './OpportunitiesView';
import { PatternView } from './PatternView';
import { FoundationCheck, SetupView } from './SetupView';
import { StatusPill } from './parts';
import { ViewTabs, ViewTrail } from './ViewTabs';
import { WaitingView } from './WaitingView';

/**
 * Experiments → Learnings, for the app's current website. Which screen shows follows the state:
 * setup until learnings are on, waiting until the first learning is written, then the three views
 * and the pages under them (a pattern, a test's debrief), all in the query string. The row above
 * every view holds where the reader is (the views' menu and the trail under it) and Ask, which opens
 * its panel beside the page.
 */
export const LearningsScreen = () => {
  const screen = useLearningsScreen();
  const manager = useLearningsPage({ device: screen.device });
  const { website, page } = manager;
  const phase = !website
    ? 'none'
    : !website.learningsEnabled
      ? 'setup'
      : !page
        ? 'loading'
        : page.status.labelled === 0
          ? 'waiting'
          : 'ready';
  // One tour per tab and page (tour-coverage.test.ts); setup and waiting are screens of their own.
  const view = screen.view;
  // The foundation check after setup is the setup screen's panel, so it shares that tour.
  const readyScreen = view.screen === 'tab' ? view.tab : view.screen === 'foundation' ? 'setup' : view.screen;
  useTourScreen(phase === 'ready' ? readyScreen : phase === 'setup' || phase === 'waiting' ? phase : null);
  const ask = useLearningsAsk({ websiteId: website?.id ?? null, enabled: phase === 'ready' });

  if (phase === 'none') return <EmptyState>Pick a website to see what its tests have taught.</EmptyState>;
  if (phase === 'setup') return <SetupScreen />;
  if (phase === 'loading' || !page) {
    return <EmptyState>{manager.isError ? 'Could not load the learnings.' : 'Loading learnings...'}</EmptyState>;
  }
  if (phase === 'waiting') return <WaitingView status={page.status} />;

  return (
    <main className="flex flex-col gap-[14px]">
      <AskRow page={page} ask={ask} screen={screen} />
      {view.screen === 'tab' && view.tab === 'learnings' && <FeedView manager={manager} page={page} screen={screen} />}
      {view.screen === 'tab' && view.tab === 'evidence' && <EvidenceView page={page} screen={screen} />}
      {view.screen === 'tab' && view.tab === 'opportunities' && (
        <OpportunitiesView manager={manager} page={page} screen={screen} />
      )}
      {view.screen === 'pattern' && (
        <PatternView manager={manager} page={page} screen={screen} patternKey={view.patternKey} />
      )}
      {view.screen === 'test' && <DebriefScreen experimentId={view.experimentId} screen={screen} />}
      {view.screen === 'foundation' && <FoundationScreen />}
      {ask.open && <LearningsAskPanel manager={ask} onOpenTest={screen.openTest} />}
    </main>
  );
};

type AskRowProps = { page: LearningsPageResponse; ask: LearningsAskManager; screen: LearningsScreenManager };

/**
 * The row above every view: the three views as tabs on the left, as the experiment page has them,
 * the status and Ask on the right, and under it, on a pattern or a debrief, the trail back (decided
 * 2026-10-20). No Create experiment here: experiments are made from an opportunity's Draft
 * experiment, or in the Library.
 */
const AskRow = ({ page, ask, screen }: AskRowProps) => (
  <div className="flex flex-col gap-2">
    <div className="flex flex-wrap items-center justify-between gap-2.5">
      <ViewTabs screen={screen} opportunities={page.opportunities.length} />
      <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2.5">
        {/* The closed Ask field's height (a 36px input, 4px padding, 1px border) and corners. */}
        <StatusPill tone="accent" className="h-[46px] gap-2 rounded-xl px-3.5 py-0">
          {page.status.labelled}/{page.status.concluded} experiments
        </StatusPill>
        <AskField manager={ask} />
      </div>
    </div>
    <ViewTrail screen={screen} patternLabel={screen.patternKey ? (page.labels[screen.patternKey] ?? null) : null} />
  </div>
);

/** Its own component so the setup hooks run only while setup shows. */
const SetupScreen = () => <SetupView setup={useLearningsSetup()} />;

const FoundationScreen = () => <FoundationCheck setup={useLearningsSetup()} />;

type DebriefScreenProps = { experimentId: number; screen: LearningsScreenManager };

const DebriefScreen = ({ experimentId, screen }: DebriefScreenProps) => (
  <DebriefView manager={useTestDebrief({ experimentId })} screen={screen} />
);
